import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — wraps a block of content with a soft scroll-triggered fade-up.
 *
 * Premium motion philosophy applied here:
 *   - No library. One IntersectionObserver instance per Reveal node. Disconnects
 *     after the first reveal so we're not paying for offscreen observation.
 *   - Triggers slightly before the element fully enters the viewport
 *     (`rootMargin: 0px 0px -10% 0px`) so the motion is already in flight by
 *     the time the user's eyes land on it.
 *   - Honors `prefers-reduced-motion` via the duration override in index.css —
 *     the element still reveals, just instantly.
 *   - `delay` enables stagger within a group: <Reveal delay={0}><Card/></Reveal>,
 *     <Reveal delay={80}><Card/></Reveal>, ... ~80–120ms is the sweet spot.
 *
 * Defaults: triggers once, soft transition, no delay.
 *
 * Usage:
 *   <Reveal>
 *     <section>...</section>
 *   </Reveal>
 *
 *   <Reveal delay={80} as="li">
 *     <ProductCard />
 *   </Reveal>
 */
export const Reveal = ({
  children,
  as: Tag = 'div',
  delay = 0,
  className = '',
  threshold = 0.12,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // If IntersectionObserver isn't available (very old browsers, some
    // headless test envs), just show the content. Better visible than blank.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -10% 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);

  return (
    <Tag
      ref={ref}
      className={`reveal-init ${visible ? 'reveal-visible' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
